import { useMsal } from '@azure/msal-react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams, useRouterState } from '@tanstack/react-router';
import { Button, IconButton } from '@volvo/vcdk-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { RouterItem } from '@/types/RouterItem';

import { withComponentErrorBoundary } from '@/components/ComponentErrorBoundary';
import ManualsSelectionModal from '@/components/modals/ManualsSelectionModal';
import PageHeader from '@/components/pageheaders/PageHeader';
import UserMetadataAvatar from '@/components/UserMetadataAvatar';
import { featuresName, isFeatureToggled } from '@/config/EnviromentConfig';
import { getIndividualProductsQueryOptions, unitByIdQueryOptions } from '@/service/vpc/queryOptions';
import { chassis, enviroment, telemetry } from '@/utils';
import { retrieveEndCustomerId } from '@/utils/account';
import { manualsAndHandbooksBaseLink, sparePartsBaseLink } from '@/utils/baseLinks';
import { hasVisibleServices } from '@/utils/services';

const FleetUnitHeader: React.FC = () => {
  const { t } = useTranslation();
  const { instance } = useMsal();
  const { fleetId, endCustomerUnitId } = useParams({
    from: '/_authenticated/_account/fleets/$fleetId/units/$endCustomerUnitId',
  });
  const page = useRouterState({
    select: (s) => s.location.state.page,
  });
  const navigate = useNavigate();
  const [showManualsModal, setShowManualsModal] = useState(false);

  const { data: unit, isLoading: isLoadingUnit } = useQuery(unitByIdQueryOptions({ endCustomerUnitId }));
  const serialNumbers = chassis.getUnitSerialNumbers(unit);
  const { data: individualProducts } = useQuery(
    getIndividualProductsQueryOptions({ serialNumbers: serialNumbers || [] })
  );

  const activeAccount = instance.getActiveAccount();
  const endCustomerId = activeAccount ? retrieveEndCustomerId(activeAccount) : undefined;
  // Easy Connect units have their digital-service provisioning hidden, so the Services tab is suppressed.
  const hideEasyConnectServices =
    isFeatureToggled(featuresName.hideEasyConnectProvisioning) &&
    telemetry.isEasyConnectUnit(unit?.telemetryDeviceInfo);

  // IPS40 units skip the ownership check, but every unit needs a service the Services page can
  // render — otherwise the tab only opens onto the empty state (AB#1640979).
  const showDigitalServices =
    unit != null &&
    hasVisibleServices(unit.serviceStatuses) &&
    !hideEasyConnectServices &&
    (chassis.isIps40(unit) || (endCustomerId != null && String(unit.ownedByEndCustomerId) === endCustomerId));

  const items: RouterItem[] = [
    {
      title: t(($) => $.pages.unit.tabs.specifications),
      to: '/fleets/$fleetId/units/$endCustomerUnitId/specifications',
      params: { fleetId, endCustomerUnitId },
    },
    {
      title: t(($) => $.pages.unit.tabs.campaigns),
      to: '/fleets/$fleetId/units/$endCustomerUnitId/campaigns',
      params: { fleetId, endCustomerUnitId },
    },
    {
      title: t(($) => $.pages.unit.tabs.coverage),
      to: '/fleets/$fleetId/units/$endCustomerUnitId/coverage',
      params: { fleetId, endCustomerUnitId },
    },
    {
      title: t(($) => $.pages.unit.tabs.maintenance_history),
      to: '/fleets/$fleetId/units/$endCustomerUnitId/maintenance',
      params: { fleetId, endCustomerUnitId },
    },
    {
      title: t(($) => $.pages.unit.tabs.diagnostics_log),
      to: '/fleets/$fleetId/units/$endCustomerUnitId/diagnostics',
      params: { fleetId, endCustomerUnitId },
      hide: !enviroment.isQA,
    },
    {
      title: t(($) => $.pages.unit.tabs.services),
      to: '/fleets/$fleetId/units/$endCustomerUnitId/services',
      params: { fleetId, endCustomerUnitId },
      hide: !showDigitalServices,
    },
    {
      title: t(($) => $.pages.unit.tabs.settings),
      to: '/fleets/$fleetId/units/$endCustomerUnitId/settings',
      params: { fleetId, endCustomerUnitId },
    },
  ];

  const handleManualsClick = () => {
    if (serialNumbers.length <= 1) {
      window.open(`${manualsAndHandbooksBaseLink}${serialNumbers.join(',')}`, '_blank', 'noopener,noreferrer');
    } else {
      setShowManualsModal(true);
    }
  };

  return (
    <>
      <PageHeader
        isLoading={isLoadingUnit}
        title={unit?.metadata?.unitName || unit?.unitName || ''}
        subTitle={`${unit?.brand || ''}, ${unit?.unitModel || ''}`}
        isClaimingUnit={unit?.claiming}
        items={items}
        backButton={
          <IconButton
            icon="arrow-left"
            size="large"
            onClick={() => navigate({ to: '/fleets/$fleetId/units', params: { fleetId }, search: { page } })}
          />
        }
        avatar={
          <UserMetadataAvatar isLoading={isLoadingUnit} size={56} showPlaceholder url={unit?.metadata?.blobUrl} />
        }
        actions={
          <>
            <Button
              variant="primary"
              size="medium"
              href={`${sparePartsBaseLink}${serialNumbers.join(',')}`}
              target="_blank"
              rel="noopener noreferrer"
              iconEnd="chevron-right"
              className="w-full md:w-fit"
            >
              {t(($) => $.pages.unit.spare_parts)}
            </Button>
            <Button variant="secondary" size="medium" onClick={handleManualsClick} className="w-full md:w-fit">
              {t(($) => $.pages.unit.tabs.manuals_handbooks)}
            </Button>
          </>
        }
        telemetryDeviceInfo={unit?.telemetryDeviceInfo}
        connectivityStatus={unit?.connectivityStatus?.status}
        serviceStatuses={unit?.serviceStatuses}
      />
      <ManualsSelectionModal
        open={showManualsModal}
        onClose={() => setShowManualsModal(false)}
        products={individualProducts || []}
      />
    </>
  );
};

export default withComponentErrorBoundary(FleetUnitHeader);
